import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import {
  PARCEL_TABLE,
  cartTotals,
  fmt,
  selectActiveTable,
  useAppStore,
} from '@restorant/core';
import { radius, useColors } from '../theme';
import { useCatLabel, useItemsLabel, useT, useTableLabel } from '../i18n';
import {
  useCategories, useDiscountPresets, useItemsInCategory, useMenuItems, useTableIds,
} from '../menu';
import { usePrompt } from '../prompt';
import { Icon } from '../icons/Icon';
import { CartRow } from '../components/CartRow';
import { CategoryCard } from '../components/CategoryCard';
import { EmptyNote } from '../components/EmptyNote';
import { ItemCard } from '../components/ItemCard';
import { ItemGrid } from '../components/ItemGrid';
import { MiniButton } from '../components/MiniButton';
import { SectionTitle } from '../components/SectionTitle';
import { StepHeader } from '../components/StepHeader';
import { T } from '../components/Text';
import { TableCard } from '../components/TableCard';
import { TextField } from '../components/TextField';

/* ---------------- step 1: pick a table ---------------- */

function TableStep() {
  const colors = useColors();
  const t = useT();
  // The floor plan lives in the database; useTableIds derives the picker order
  // from it (see buildTableIds on why a selector must not allocate).
  const tableIds = useTableIds();
  const tables = useAppStore((s) => s.tables);
  const openTable = useAppStore((s) => s.openTable);
  const addCustomTable = useAppStore((s) => s.addCustomTable);
  const openPrompt = usePrompt();

  return (
    <>
      <SectionTitle
        title={t('select_table')}
        icon="table"
        hint={t('select_table_hint')}
        first
      />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {tableIds.map((id) => (
          <TableCard
            key={id}
            tableId={id}
            open={tables[id] ?? null}
            onPress={() => openTable(id)}
          />
        ))}

        <Pressable
          onPress={() =>
            openPrompt({
              title: t('add_table_title'),
              hint: t('add_table_hint'),
              mode: 'text',
              submitLabel: t('add_table'),
              onSubmit: addCustomTable,
            })
          }
          style={({ hovered, pressed }: any) => ({
            flexGrow: 1,
            flexBasis: 150,
            maxWidth: 210,
            minHeight: 112,
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: 14,
            borderRadius: radius.lg,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: hovered ? colors.gold : colors.line,
            opacity: pressed ? 0.8 : 1,
          })}
        >
          <Icon name="plus" size={20} color={colors.textFaint} />
          <T size={12.5} weight={600} color={colors.textFaint}>
            {t('add_table')}
          </T>
        </Pressable>
      </View>
    </>
  );
}

/* ---------------- step 2: pick a category ---------------- */

function CategoryStep({ itemCount }: { itemCount: number }) {
  const t = useT();
  const tableLabel = useTableLabel();
  const activeTable = useAppStore((s) => s.activeTable);
  const offers = useAppStore((s) => s.offers);
  const selectFlowCat = useAppStore((s) => s.selectFlowCat);
  const goBack = useAppStore((s) => s.goBack);
  const categories = useCategories();
  const menuItems = useMenuItems();

  return (
    <>
      <StepHeader
        title={activeTable ? tableLabel(activeTable) : ''}
        subtitle={t('select_category_hint')}
        onBack={goBack}
        itemCount={itemCount}
      />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {categories.map((cat) => {
          const items = menuItems.filter((i) => i.cat === cat.id);
          return (
            <CategoryCard
              key={cat.id}
              cat={cat}
              itemCount={items.length}
              offerCount={items.filter((i) => (offers[i.id] ?? 0) > 0).length}
              onPress={() => selectFlowCat(cat.id)}
            />
          );
        })}
      </View>
    </>
  );
}

/* ---------------- step 3: add items ---------------- */

function ItemsStep({ itemCount }: { itemCount: number }) {
  const t = useT();
  const catLabel = useCatLabel();
  const tableLabel = useTableLabel();
  const flowCat = useAppStore((s) => s.flowCat);
  const activeTable = useAppStore((s) => s.activeTable);
  const goBack = useAppStore((s) => s.goBack);
  const items = useItemsInCategory(flowCat);

  return (
    <>
      <StepHeader
        title={flowCat ? catLabel(flowCat) : ''}
        subtitle={activeTable ? tableLabel(activeTable) : ''}
        onBack={goBack}
        itemCount={itemCount}
      />
      {items.length ? (
        <ItemGrid>
          {items.map((item) => (
            <ItemCard key={item.id} item={item} mode="order" />
          ))}
        </ItemGrid>
      ) : (
        <EmptyNote>{t('no_match')}</EmptyNote>
      )}
    </>
  );
}

/* ---------------- step 4: review and place ---------------- */

function TotalLine({
  label,
  value,
  tone = 'normal',
}: {
  label: string;
  value: string;
  tone?: 'normal' | 'good' | 'grand';
}) {
  const colors = useColors();
  const grand = tone === 'grand';
  const color = grand ? colors.gold : tone === 'good' ? colors.good : colors.textDim;
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: grand ? 12 : 7,
        marginTop: grand ? 6 : 0,
        borderTopWidth: grand ? 1 : 0,
        borderTopColor: colors.line,
      }}
    >
      <T size={grand ? 18 : 13} weight={grand ? 700 : 500} color={color} serif={grand}>
        {label}
      </T>
      <T size={grand ? 18 : 13} weight={grand ? 700 : 600} color={color}>
        {value}
      </T>
    </View>
  );
}

function CartStep() {
  const colors = useColors();
  const t = useT();
  const tableLabel = useTableLabel();
  const table = useAppStore(selectActiveTable);
  const activeTable = useAppStore((s) => s.activeTable);
  const changeQty = useAppStore((s) => s.changeQty);
  const setOrderDiscount = useAppStore((s) => s.setOrderDiscount);
  const setCustomer = useAppStore((s) => s.setCustomer);
  const clearTable = useAppStore((s) => s.clearTable);
  const goBack = useAppStore((s) => s.goBack);
  const openPrompt = usePrompt();
  const presets = useDiscountPresets();
  const maxPct = useAppStore((s) => s.settings.maxDiscountPct);

  const cart = table?.cart ?? [];
  const discount = table?.discount ?? 0;
  const { sub, disc, grand, itemSavings, qty } = cartTotals(cart, discount);
  const empty = cart.length === 0;

  return (
    <>
      <StepHeader
        title={t('cart')}
        subtitle={activeTable ? tableLabel(activeTable) : ''}
        onBack={goBack}
        itemCount={qty}
      />

      {empty ? (
        <EmptyNote>{t('empty_cart')}</EmptyNote>
      ) : (
        <View style={{ gap: 20 }}>
          <View
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: radius.lg,
              paddingHorizontal: 16,
              paddingVertical: 6,
            }}
          >
            {cart.map((line, idx) => (
              <CartRow
                key={`${line.itemId}-${line.variant}`}
                line={line}
                isLast={idx === cart.length - 1}
                onChangeQty={(d) => changeQty(idx, d)}
              />
            ))}
          </View>

          <View
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: radius.lg,
              padding: 16,
              gap: 14,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="user" size={15} color={colors.gold} />
              <T size={13.5} weight={700}>
                {t('customer_details')}
              </T>
              <T size={11.5} color={colors.textFaint}>
                ({t('optional')})
              </T>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
              <TextField
                testID="customer-name"
                label={t('customer_name')}
                icon="user"
                value={table?.customer.name ?? ''}
                onChangeText={(name) => setCustomer({ name })}
              />
              <TextField
                testID="customer-phone"
                label={t('customer_phone')}
                icon="phone"
                keyboardType="phone-pad"
                value={table?.customer.phone ?? ''}
                onChangeText={(phone) => setCustomer({ phone })}
              />
            </View>
          </View>

          <View
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: radius.lg,
              padding: 16,
              gap: 12,
            }}
          >
            <View style={{ flexDirection: 'row', gap: 6 }}>
              {presets.map((pct) => (
                <MiniButton
                  key={pct}
                  label={`${pct}%`}
                  active={discount === pct}
                  onPress={() => setOrderDiscount(pct)}
                />
              ))}
              <MiniButton
                label={t('custom')}
                onPress={() =>
                  openPrompt({
                    title: t('custom_discount_for', { name: t('total') }),
                    hint: t('discount_range_hint', { max: maxPct }),
                    initial: discount ? String(discount) : '',
                    onSubmit: (v) => setOrderDiscount(Number(v)),
                  })
                }
              />
              {discount > 0 ? (
                <MiniButton label={t('clear')} onPress={() => setOrderDiscount(0)} />
              ) : null}
            </View>

            <View>
              <TotalLine label={t('subtotal')} value={fmt(sub)} />
              {itemSavings > 0 ? (
                <TotalLine label={t('item_savings')} value={`−${fmt(itemSavings)}`} tone="good" />
              ) : null}
              {disc > 0 ? (
                <TotalLine
                  label={`${t('discount')} (${discount}%)`}
                  value={`−${fmt(disc)}`}
                  tone="good"
                />
              ) : null}
              <TotalLine label={t('total')} value={fmt(grand)} tone="grand" />
            </View>

            <Pressable
              onPress={() => clearTable()}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, paddingVertical: 6 }}
            >
              <Icon name="trash" size={14} color={colors.spice} />
              <T size={12.5} weight={700} color={colors.spice}>
                {t('clear_table')}
              </T>
            </Pressable>
          </View>
        </View>
      )}
    </>
  );
}

/* ---------------- sticky action bar ---------------- */

/**
 * The primary action never scrolls away: it is pinned below the flow, above the
 * bottom nav. Browsing steps show the running total plus "View order"; the
 * review step shows "Place Order".
 */
function OrderActionBar({ contentPadding }: { contentPadding: number }) {
  const colors = useColors();
  const t = useT();
  const items = useItemsLabel();
  const step = useAppStore((s) => s.orderStep);
  const table = useAppStore(selectActiveTable);
  const goToStep = useAppStore((s) => s.goToStep);
  const placeOrder = useAppStore((s) => s.placeOrder);

  const { grand, qty } = cartTotals(table?.cart ?? [], table?.discount ?? 0);

  // Nothing to act on while picking a table, or while the order is empty.
  if (step === 'tables' || qty === 0) return null;

  const placing = step === 'cart';

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: contentPadding,
        paddingVertical: 12,
        borderTopWidth: 1,
        borderTopColor: colors.line,
        backgroundColor: colors.surface,
      }}
    >
      <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <T size={11.5} color={colors.textFaint}>
          {items(qty)} {t('items_in_order')}
        </T>
        <T size={18} weight={700} serif color={colors.gold}>
          {fmt(grand)}
        </T>
      </View>

      <Pressable
        onPress={placing ? placeOrder : () => goToStep('cart')}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 9,
          paddingVertical: 14,
          paddingHorizontal: 20,
          borderRadius: radius.md,
          backgroundColor: colors.gold,
          opacity: pressed ? 0.85 : 1,
        })}
      >
        <Icon name={placing ? 'circleCheck' : 'order'} size={18} color={colors.onGold} />
        <T size={14.5} weight={800} color={colors.onGold}>
          {placing ? t('place_order') : t('view_cart')}
        </T>
      </Pressable>
    </View>
  );
}

/* ---------------- flow shell ---------------- */

export function OrderScreen({ contentPadding }: { contentPadding: number; width: number }) {
  const step = useAppStore((s) => s.orderStep);
  const table = useAppStore(selectActiveTable);
  const itemCount = cartTotals(table?.cart ?? [], 0).qty;

  return (
    <View style={{ flex: 1, minHeight: 0 }}>
      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{ padding: contentPadding, paddingBottom: 28 }}
        showsVerticalScrollIndicator={false}
      >
        {step === 'tables' ? <TableStep /> : null}
        {step === 'categories' ? <CategoryStep itemCount={itemCount} /> : null}
        {step === 'items' ? <ItemsStep itemCount={itemCount} /> : null}
        {step === 'cart' ? <CartStep /> : null}
      </ScrollView>
      <OrderActionBar contentPadding={contentPadding} />
    </View>
  );
}

export { PARCEL_TABLE };
